import { Button } from '@/components/ui/button';
import { Minus, Plus, Trash2, Edit3 } from 'lucide-react';
import { formatPrice } from '@/utils/shopFormatters';
import GlassImage from '@/components/shared/GlassImage';
import type { CartItem } from '@/types/cart';
import { trackEvent } from '@/utils/analytics';
import { userUtils } from '@/utils/auth';

interface CartItemCardProps {
  item: CartItem;
  onQuantityChange: (itemId: string, newQuantity: number) => void;
  onEditVariants: (itemId: string) => void;
  onRemove: (itemId: string) => void;
}

export function CartItemCard({ item, onQuantityChange, onEditVariants, onRemove }: CartItemCardProps) {
  const user = userUtils.getUser();
  const handleIncrement = () => {
    onQuantityChange(item.id, item.quantity + 1);
  };

  const handleDecrement = () => {
    if (item.quantity > 1) {
      onQuantityChange(item.id, item.quantity - 1);
    }
  };

  const handleRemove = () => {
    if (user) {
      trackEvent('remove_from_cart', {
        userId: user?._id,
        email: user?.email,
        name: user?.firstName + ' ' + user?.lastName,
        username: user?.username,
        country: user?.country,
        isActive: user?.isActive,
        isEmailVerified: user?.emailVerified,
        isPhoneVerified: user?.phoneVerified,
        isDisabled: user?.isDisabled,
        isNewBuyer: user?.isNewBuyer,
        isIdentityVerified: user?.isIdentityVerified,
        productId: item?.productId,
        productName: item?.productName,
        productImage: item?.productImage,
        price: item?.price,
        originalPrice: item?.originalPrice,
        discountPercentage: item?.discountPercentage,
        quantity: item?.quantity,
        sellerId: item?.sellerId,
        sellerName: item?.sellerName,
        weight: item?.weight,
      });
    }
    onRemove(item.id);
  };

  // Get variant display text
  const variantText = item.selectedVariantPath
    ? [
        item.selectedVariantPath.variantValue,
        item.selectedVariantPath.subVariantValue,
        item.selectedVariantPath.subSubVariantValue,
      ]
        .filter(Boolean)
        .join(' / ')
    : null;

  const hasVariants = !!variantText;

  return (
    <div className="flex gap-3 sm:gap-4">
      {/* Product Image */}
      <GlassImage
        src={item.productImage}
        alt={item.productName}
        containerClassName="w-20 h-20 sm:w-28 sm:h-28 flex-shrink-0 rounded-[10px] bg-gray-100"
        imageClassName="w-full h-full object-cover"
        blurIntensity={10}
        tint="dark"
        placeholderColor="rgba(200, 200, 200, 0.5)"
        loading="lazy"
        decoding="async"
      />

      {/* Product Details */}
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h3 className="font-lexend text-[0.8rem] sm:text-[0.875rem] font-light tracking-[-0.14px] leading-[1.4] capitalize text-black line-clamp-2">
              {item.productName}
            </h3>
            {/* Show selected variants if any with Edit button */}
            {hasVariants && (
              <div className="flex items-center gap-2 mt-1">
                <p className="font-lexend text-[0.7rem] sm:text-[0.75rem] font-normal text-black/60 flex-1">
                  {variantText}
                </p>
                <button
                  onClick={() => onEditVariants(item.id)}
                  className="flex items-center gap-1 text-[0.7rem] sm:text-[0.75rem] text-primary-green hover:text-primary-green/80 font-medium transition-colors flex-shrink-0 cursor-pointer"
                  title="Edit variant selection"
                >
                  <Edit3 size={11} className="sm:w-3 sm:h-3" />
                  <span>Edit</span>
                </button>
              </div>
            )}
          </div>

          {/* Remove Button */}
          <Button
            onClick={handleRemove}
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 hover:bg-red-50 hover:text-red-600 transition-colors"
            aria-label="Remove item"
          >
            <Trash2 size={14} strokeWidth={1.5} />
          </Button>
        </div>

        {/* Quantity Controls */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-0 rounded-[4px] border border-black/70">
            <Button
              onClick={handleDecrement}
              disabled={item.quantity <= 1}
              variant="ghost"
              size="sm"
              className="h-6 w-6 sm:h-7 sm:w-7 p-0 rounded-none hover:bg-gray-100 disabled:opacity-30"
            >
              <Minus size={12} className="sm:w-3 sm:h-3" strokeWidth={1.25} />
            </Button>

            <span className="font-lexend text-[0.9rem] sm:text-[1rem] font-normal tracking-[-0.16px] capitalize text-black px-2 sm:px-3">
              {item.quantity}
            </span>

            <Button
              onClick={handleIncrement}
              variant="ghost"
              size="sm"
              className="h-6 w-6 sm:h-7 sm:w-7 p-0 rounded-none hover:bg-gray-100"
            >
              <Plus size={12} className="sm:w-3 sm:h-3" strokeWidth={1.25} />
            </Button>
          </div>
        </div>

        {/* Price */}
        <div className="flex items-center justify-between">
          <div className="font-lexend text-[1.1rem] sm:text-[1.25rem] font-semibold tracking-[-0.22px] capitalize text-black">
            {formatPrice(item.totalPrice)}
          </div>
          
          {/* Show discount if applicable */}
          {item.discountPercentage > 0 && (
            <span className="font-lexend text-[0.7rem] sm:text-[0.75rem] font-normal text-[#F12626]">
              {item.discountPercentage}% off
            </span>
          )}
        </div>
      </div>
    </div>
  );
}